<template>
    <div class="call-recording-player">
        <b-alert v-if="errorLoading" variant="danger" show dismissible @dismissed="errorLoading = false"
            class="py-2 mb-2">
            Failed to load audio.
            <a href="#" @click.prevent="loadAudio()" class="alert-link">Retry</a>
        </b-alert>

        <div v-else class="crp-bar" :class="{ 'crp-disabled': !isReady }">
            <!-- Scrubber on its own line: elapsed | seek bar | remaining -->
            <div class="crp-scrubber-row">
                <span class="crp-time">{{ currentTimeLabel }}</span>
                <div class="crp-scrubber" role="slider" tabindex="-1" aria-label="Seek" :aria-valuemin="0"
                    :aria-valuemax="durationRounded" :aria-valuenow="currentTimeRounded" :aria-valuetext="positionLabel"
                    @pointerdown="onScrubberDown" @pointermove="onScrubberMove" @pointerup="onScrubberUp">
                    <div class="crp-track"></div>
                    <div v-for="(seg, i) in bufferedSegments" :key="'buf-' + i" class="crp-buffered"
                        :style="{ left: seg.left + '%', width: seg.width + '%' }"></div>
                    <div class="crp-fill" :style="{ width: progressPercent + '%' }"></div>
                    <div class="crp-thumb" :style="{ left: progressPercent + '%' }"></div>
                </div>
                <span class="crp-time crp-time-remaining">-{{ remainingTimeLabel }}</span>
            </div>

            <!-- Transport row: rewind controls | play/pause | forward controls -->
            <div class="crp-controls-row">
                <b-button class="crp-skip" variant="outline-secondary" size="sm" :disabled="!isReady" v-b-tooltip.hover
                    title="Rewind 30 seconds" aria-label="Rewind 30 seconds" @click="skip(-30)">
                    <i class="fas fa-angle-double-left" aria-hidden="true"></i> 30s
                </b-button>
                <b-button class="crp-skip" variant="outline-secondary" size="sm" :disabled="!isReady" v-b-tooltip.hover
                    title="Rewind 10 seconds (←)" aria-label="Rewind 10 seconds" @click="skip(-10)">
                    <i class="fas fa-angle-left" aria-hidden="true"></i> 10s
                </b-button>

                <b-button class="crp-play" variant="primary" :disabled="!isReady"
                    v-b-tooltip.hover="isPlaying ? 'Pause (Space)' : 'Play (Space)'"
                    :aria-label="isPlaying ? 'Pause' : 'Play'" @click="togglePlay">
                    <transition name="crp-icon" mode="out-in">
                        <i :key="isPlaying ? 'pause' : 'play'"
                            :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'" aria-hidden="true"></i>
                    </transition>
                </b-button>

                <b-button class="crp-skip" variant="outline-secondary" size="sm" :disabled="!isReady" v-b-tooltip.hover
                    title="Forward 10 seconds (→)" aria-label="Forward 10 seconds" @click="skip(10)">
                    10s <i class="fas fa-angle-right" aria-hidden="true"></i>
                </b-button>
                <b-button class="crp-skip" variant="outline-secondary" size="sm" :disabled="!isReady" v-b-tooltip.hover
                    title="Forward 30 seconds" aria-label="Forward 30 seconds" @click="skip(30)">
                    30s <i class="fas fa-angle-double-right" aria-hidden="true"></i>
                </b-button>
            </div>

            <!-- Playback speed on its own line -->
            <div class="crp-speed-row">
                <span class="crp-speed-label small text-muted">Speed</span>
                <button v-for="rate in playbackRateOptions" :key="rate" type="button"
                    class="crp-speed-link" :class="{ 'crp-speed-active': rate === playbackRate }"
                    :disabled="!isReady" :aria-pressed="rate === playbackRate"
                    :title="`Play at ${rate}× speed`" @click="setRate(rate)">{{ rate }}×</button>
                <span v-if="loading" class="crp-loading small text-muted ml-2">Loading…</span>
            </div>
        </div>

        <!-- Native element is the playback engine only; all chrome is custom above. -->
        <audio v-if="audioUrl" ref="audio" :src="audioUrl" preload="metadata"
            @loadedmetadata="onLoadedMetadata" @timeupdate="onTimeUpdate" @progress="updateBuffered"
            @play="onPlay" @pause="onPause" @ended="onEnded" @error="onError"></audio>

        <h6 class="crp-transcript-heading">Transcription</h6>
        <transcription-viewer :entity-type="0" :entity-id="callId" :current-time="transcriptCurrentTime"
            :compact="transcriptCompact" @transcriptclick="onTranscriptClick" />
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import TranscriptionViewer from '@/components/TranscriptionViewer.vue';

// Only one player should respond to keyboard shortcuts at a time, and only one should be audibly
// playing. These module-level singletons coordinate that across every mounted player instance
// (the table can expand several rows at once, each its own player).
let activeKeyboardPlayer: CallRecordingPlayer | null = null;
let currentlyPlaying: CallRecordingPlayer | null = null;

// One shared keydown listener routes to whichever player is active. Registering per-instance
// would mean N expanded rows each add a window listener that fires and early-returns on every
// keystroke; a single dispatcher does the same work once.
window.addEventListener('keydown', (e) => activeKeyboardPlayer?.handleKeydown(e));

@Component({
    name: 'CallRecordingPlayer',
    components: { TranscriptionViewer },
})
export default class CallRecordingPlayer extends Vue {
    /** Twilio recording SID; the key GetAudioUrl resolves to a presigned S3 URL. */
    @Prop({ required: true }) private recordingSid!: string;
    /** CallLog row id; the transcript entity id (calls use entity type 0). */
    @Prop({ required: true }) private callId!: number;
    /** Mirrors the page-level Compact/Expanded transcript toggle. */
    @Prop({ default: false }) private transcriptCompact!: boolean;

    protected playbackRateOptions: number[] = [0.5, 1, 1.5, 2];
    protected playbackRate: number = 1;

    protected audioUrl: string = '';
    protected loading: boolean = false;
    protected errorLoading: boolean = false;

    protected isPlaying: boolean = false;
    protected currentTime: number = 0;
    protected duration: number = 0;

    // Snapshot of the media element's downloaded ranges (seconds), used to draw the buffered bar.
    protected bufferedRanges: Array<{ start: number; end: number }> = [];

    private scrubbing: boolean = false;
    // Caps the auto-refresh-on-error so a permanently bad URL can't loop forever.
    private autoRetries: number = 0;

    mounted() {
        this.loadAudio();
    }

    beforeDestroy() {
        if (activeKeyboardPlayer === this) activeKeyboardPlayer = null;
        if (currentlyPlaying === this) currentlyPlaying = null;
    }

    // The modal reuses one instance across different deep-linked calls; reset and reload on change.
    @Watch('recordingSid')
    onRecordingSidChanged() {
        this.audioUrl = '';
        this.duration = 0;
        this.currentTime = 0;
        this.bufferedRanges = [];
        this.isPlaying = false;
        this.errorLoading = false;
        this.autoRetries = 0;
        this.loadAudio();
    }

    private get audioEl(): HTMLAudioElement | null {
        return (this.$refs.audio as HTMLAudioElement) ?? null;
    }

    get isReady(): boolean {
        return this.duration > 0;
    }

    get progressPercent(): number {
        return this.duration > 0 ? Math.min(100, (this.currentTime / this.duration) * 100) : 0;
    }

    // Downloaded ranges mapped to scrubber percentages, mirroring the native element's buffered bar.
    // There can be several disjoint ranges once the user seeks around, so each is drawn separately.
    get bufferedSegments(): Array<{ left: number; width: number }> {
        if (this.duration <= 0) return [];
        return this.bufferedRanges.map((r) => ({
            left: Math.max(0, (r.start / this.duration) * 100),
            width: Math.max(0, Math.min(100, ((r.end - r.start) / this.duration) * 100)),
        }));
    }

    get currentTimeLabel(): string {
        return this.formatTime(this.currentTime);
    }

    get durationLabel(): string {
        return this.formatTime(this.duration);
    }

    // Shown on the right of the scrubber as a countdown to the end of the recording.
    get remainingTimeLabel(): string {
        const remaining = this.duration > 0 ? Math.max(0, this.duration - this.currentTime) : 0;
        return this.formatTime(remaining);
    }

    get currentTimeRounded(): number {
        return Math.round(this.currentTime);
    }

    get durationRounded(): number {
        return Math.round(this.duration) || 0;
    }

    get positionLabel(): string {
        return `${this.currentTimeLabel} of ${this.durationLabel}`;
    }

    // Transcript highlighting tracks only while this player is actually playing, matching the
    // prior behavior (highlight clears on pause). Skipping while playing keeps it in sync for free.
    get transcriptCurrentTime(): number | null {
        return this.isPlaying ? this.currentTime : null;
    }

    async loadAudio() {
        if (this.loading) return;
        this.loading = true;
        this.errorLoading = false;
        try {
            // Each call returns a fresh presigned URL (new signature/expiry), so the string is
            // already unique — never append our own query param, which would break the S3 signature.
            const response = await Axios.get(`/Call/GetAudioUrl/${this.recordingSid}`);
            this.audioUrl = response.data.url;
            await this.$nextTick();
            const a = this.audioEl;
            if (a) {
                a.playbackRate = this.playbackRate;
                // Force a (re)load: if the URL string is unchanged (e.g. a same-second refresh),
                // reassigning :src is a no-op, so load() is what re-fetches the media.
                a.load();
            }
        } catch {
            this.errorLoading = true;
        } finally {
            this.loading = false;
        }
    }

    onLoadedMetadata() {
        const a = this.audioEl;
        this.duration = a && isFinite(a.duration) ? a.duration : 0;
        this.autoRetries = 0;
        this.updateBuffered();
    }

    onTimeUpdate() {
        const a = this.audioEl;
        if (a) this.currentTime = a.currentTime;
    }

    // Fires on the media element's `progress` event (and after metadata) as more data downloads.
    updateBuffered() {
        const a = this.audioEl;
        if (!a) {
            this.bufferedRanges = [];
            return;
        }
        const ranges: Array<{ start: number; end: number }> = [];
        for (let i = 0; i < a.buffered.length; i++) {
            ranges.push({ start: a.buffered.start(i), end: a.buffered.end(i) });
        }
        this.bufferedRanges = ranges;
    }

    onPlay() {
        this.isPlaying = true;
        this.markActive();
        if (currentlyPlaying && currentlyPlaying !== this) currentlyPlaying.pause();
        currentlyPlaying = this;
    }

    onPause() {
        this.isPlaying = false;
        if (currentlyPlaying === this) currentlyPlaying = null;
    }

    onEnded() {
        this.isPlaying = false;
        if (currentlyPlaying === this) currentlyPlaying = null;
    }

    // A presigned URL can expire (or fail) mid-session; refresh once before surfacing an error.
    onError() {
        if (!this.audioUrl) return;
        if (this.autoRetries < 1) {
            this.autoRetries++;
            this.loadAudio();
        } else {
            this.errorLoading = true;
        }
    }

    pause() {
        this.audioEl?.pause();
    }

    togglePlay() {
        const a = this.audioEl;
        if (!a) return;
        this.markActive();
        if (a.paused) {
            a.play();
        } else {
            a.pause();
        }
    }

    // Seek to an absolute time. Mirroring it into currentTime keeps the scrubber (and, while
    // playing, the transcript) in sync even when paused. Single place every seek path goes through.
    private seekTo(seconds: number) {
        const a = this.audioEl;
        if (!a) return;
        a.currentTime = seconds;
        this.currentTime = seconds;
    }

    // Skip forward/back by seconds, clamped to [0, duration].
    skip(seconds: number) {
        const a = this.audioEl;
        if (!a) return;
        this.markActive();

        let target = a.currentTime + seconds;
        if (target < 0) target = 0;
        if (isFinite(a.duration) && target > a.duration) target = a.duration;
        this.seekTo(target);
    }

    setRate(rate: number) {
        this.playbackRate = rate;
        const a = this.audioEl;
        if (a) a.playbackRate = rate;
        this.markActive();
    }

    onScrubberDown(e: PointerEvent) {
        if (!this.isReady) return;
        this.scrubbing = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        this.markActive();
        this.seekFromClientX(e.clientX, e.currentTarget as HTMLElement);
    }

    onScrubberMove(e: PointerEvent) {
        if (!this.scrubbing) return;
        this.seekFromClientX(e.clientX, e.currentTarget as HTMLElement);
    }

    onScrubberUp() {
        this.scrubbing = false;
    }

    private seekFromClientX(clientX: number, bar: HTMLElement) {
        const a = this.audioEl;
        if (!a || this.duration <= 0) return;
        const rect = bar.getBoundingClientRect();
        let fraction = (clientX - rect.left) / rect.width;
        fraction = Math.max(0, Math.min(1, fraction));
        this.seekTo(fraction * this.duration);
    }

    onTranscriptClick(_entityId: number, startTime: string) {
        const a = this.audioEl;
        if (!a) return;
        this.seekTo(moment.duration(startTime).asSeconds());
        if (a.paused) a.play();
    }

    // Invoked by the shared module-level listener, but only ever for the active player.
    handleKeydown(e: KeyboardEvent) {
        // Don't hijack typing or a control's own key handling (search box, audit/notes fields, etc.).
        const target = e.target as HTMLElement | null;
        if (target) {
            const tag = target.tagName.toLowerCase();
            const isInteractive = tag === 'input' || tag === 'textarea' || tag === 'select'
                || tag === 'button' || tag === 'a' || tag === 'audio' || target.isContentEditable;
            if (isInteractive) return;
        }

        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                this.skip(-10);
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.skip(10);
                break;
            case ' ':
            case 'Spacebar': // legacy key name for older browsers
                e.preventDefault();
                this.togglePlay();
                break;
        }
    }

    private markActive() {
        activeKeyboardPlayer = this;
    }

    private formatTime(seconds: number): string {
        if (!isFinite(seconds) || seconds < 0) return '0:00';
        const total = Math.floor(seconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
}
</script>

<style scoped>
.call-recording-player {
    width: 100%;
    /* Neutralize the modal body's tall line-height (1.9) so the player's rows are
       spaced identically in the deep-link modal and the table row. */
    line-height: 1.5;
}

.crp-bar {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.625rem 0.875rem;
    margin-bottom: 0.75rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
}

.crp-disabled {
    opacity: 0.7;
}

.crp-scrubber-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    /* Pin so the row hugs the slider; otherwise inherited line-height inflates it
       (taller in the table than the modal). */
    line-height: 1;
}

.crp-controls-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    /* A touch more separation from the scrubber than the tighter controls/speed gap. */
    margin-top: 0.25rem;
}

.crp-speed-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    /* A little extra separation from the transport controls above. */
    margin-top: 0.4rem;
}

/* Compact jump buttons: smaller than Bootstrap's size="sm" so they read as
   secondary to the central play control. */
.crp-skip {
    /* now-ui's global `.btn { margin: 10px 1px }` is added to the flex line's cross-size,
       which inflated the controls row only where that margin survives (the modal). Zero it so
       row height is purely the buttons' own size and identical in the table and modal. */
    margin: 0;
    padding: 0.15rem 0.4rem;
    font-size: 0.72rem;
    line-height: 1.2;
}

/* Larger circular primary control, flanked by the jump buttons. */
.crp-play {
    /* Drop now-ui's global `.btn` vertical margin (10px) — it was the 20px that made the controls
       row 68px in the modal vs 48px in the table. */
    margin: 0;
    width: 3rem;
    height: 3rem;
    padding: 0 !important;
    border-radius: 50%;
    font-size: 1.15rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    /* Matches the global search placeholder color (#3c5068). */
    background-color: #3c5068 !important;
    border-color: #3c5068 !important;
}

.crp-play:hover:not(:disabled),
.crp-play:focus:not(:disabled) {
    background-color: #324354 !important;
    border-color: #324354 !important;
}

/* Lively swap between the play and pause glyphs (Vue 2 transition class names). */
.crp-icon-enter-active,
.crp-icon-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.crp-icon-enter,
.crp-icon-leave-to {
    opacity: 0;
    transform: scale(0.4) rotate(-90deg);
}

.crp-time {
    font-size: 0.8rem;
    line-height: 1;
    color: #495057;
    min-width: 3.2em;
    text-align: center;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.crp-scrubber {
    position: relative;
    flex: 1 1 auto;
    min-width: 120px;
    height: 18px;
    display: flex;
    align-items: center;
    cursor: pointer;
    /* Let pointer-drag scrub without the page scrolling on touch devices. */
    touch-action: none;
}

.crp-track {
    position: absolute;
    left: 0;
    right: 0;
    height: 5px;
    background: #dee2e6;
    border-radius: 3px;
}

/* Downloaded (buffered) portion: sits above the track, below the played fill. */
.crp-buffered {
    position: absolute;
    height: 5px;
    background: #adb5bd;
    border-radius: 3px;
    pointer-events: none;
}

.crp-fill {
    position: absolute;
    left: 0;
    height: 5px;
    background: #3c5068;
    border-radius: 3px;
    pointer-events: none;
}

.crp-thumb {
    position: absolute;
    width: 13px;
    height: 13px;
    background: #3c5068;
    border: 2px solid #fff;
    border-radius: 50%;
    transform: translateX(-50%);
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
    pointer-events: none;
}

.crp-speed-label {
    white-space: nowrap;
    margin-right: 0.25rem;
}

/* Rate options rendered as small text links rather than a dropdown. */
.crp-speed-link {
    padding: 0 0.25rem;
    border: none;
    background: none;
    font-size: 0.8rem;
    line-height: 1;
    color: #3c5068;
    cursor: pointer;
}

.crp-speed-link:hover:not(:disabled):not(.crp-speed-active) {
    text-decoration: underline;
}

.crp-speed-link:disabled {
    color: #adb5bd;
    cursor: default;
}

/* The selected rate is de-emphasized as plain bold text, not a clickable link. */
.crp-speed-active {
    color: #3c5068;
    font-weight: 700;
    cursor: default;
}

.crp-transcript-heading {
    font-weight: 500;
}
</style>
